import { QueryFunctionContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { FetchError, GetPlanDto, queryKeys, UrlUtilities, useAccessToken, type QueryParams } from "src/http";

const useGetPlanRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getPlanRequest({
    queryKey,
  }: QueryFunctionContext<ReturnType<typeof queryKeys.plans.getPlanWithOptions>>): Promise<GetPlanDto> {
    const accessToken = await getAccessToken();
    const [{ planGuid, date, offsetInMinutes, queryParams }] = queryKey;

    const url = new URL(`${configuration.apiRootUrl}/plans/${planGuid}`);

    if (date) {
      url.searchParams.append("date", date);
    }

    if (offsetInMinutes) {
      url.searchParams.append("offsetInMinutes", offsetInMinutes.toString());
    }

    UrlUtilities.appendQueryParams(url, queryParams);

    const response = await fetch(url.href, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getPlanRequest };
};

const useGetPlan = (planGuid: string, date?: string, offsetInMinutes?: number, queryParams?: QueryParams) => {
  const { getPlanRequest } = useGetPlanRequest();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.plans.getPlanWithOptions(planGuid, date, offsetInMinutes, queryParams),
    queryFn: getPlanRequest,
    onSuccess: (data) => {
      data.blocks
        .flatMap((block) => block.sections.flatMap((section) => section.actionItems))
        .forEach((actionItem) => queryClient.setQueryData(queryKeys.actionItems.summary(actionItem.guid), actionItem));
    },
  });
};

export { useGetPlan };
